import { useState, useEffect, useCallback, useRef } from "react";
import { useAuthStore } from "../stores/authStore";
import { ACTION_KEYS } from "../constants/actions";
import {
  Clock,
  Loader2,
  RefreshCw,
  Database,
  ChevronDown,
  ChevronRight,
  X,
  Search,
  CheckSquare,
  Square,
  Download,
  PhoneIncoming,
  PhoneOutgoing,
  Voicemail,
  PhoneMissed,
  AlertTriangle,
  Beaker,
  Mail,
  Bot,
} from "lucide-react";
import { SendEmailReportModal } from "../components/SendEmailReportModal";
import { toast } from "react-hot-toast";
import { format } from "date-fns";
import { zonedTimeToUtc, utcToZonedTime } from "date-fns-tz";
import * as XLSX from "xlsx";
// Default timezone for consistent display across all users
const DEFAULT_TIMEZONE = "America/Los_Angeles"; // Pacific Time

/**
 * Format a UTC date string to Pacific Time
 */
function formatInPacific(dateString: string, formatStr: string = "MMM dd, HH:mm"): string {
  const utcDate = new Date(dateString);
  const pacificDate = utcToZonedTime(utcDate, DEFAULT_TIMEZONE);
  return format(pacificDate, formatStr);
}

/**
 * Format a UTC date string in the given timezone (falls back to Pacific if not set).
 */
function formatInTimezone(dateString: string, timezone: string | null | undefined, formatStr: string = "yyyy-MM-dd HH:mm:ss"): string {
  const tz = timezone || DEFAULT_TIMEZONE;
  const utcDate = new Date(dateString);
  const zoned = utcToZonedTime(utcDate, tz);
  return format(zoned, formatStr);
}

/**
 * Return a short label for a timezone (e.g. "America/New_York" → "ET").
 * Falls back to the raw IANA string if no short mapping exists.
 */
function tzLabel(timezone: string | null | undefined): string {
  const tz = timezone || DEFAULT_TIMEZONE;
  const SHORT: Record<string, string> = {
    "America/Los_Angeles": "PT",
    "America/Denver":      "MT",
    "America/Chicago":     "CT",
    "America/New_York":    "ET",
    "America/Phoenix":     "AZ",
    "America/Anchorage":   "AKT",
    "Pacific/Honolulu":    "HT",
    "UTC":                 "UTC",
  };
  return SHORT[tz] ?? tz;
}

interface WebhookCallRecord {
  callId: string;
  queueName: string | null;
  queueId: string | null;
  startedAt: string;
  answeredAt: string | null;
  endedAt: string;
  externalTransferredAt: string | null;
  billableMinutes: number;
  billableMilliseconds: number;
  durationSeconds: number;
  afterCallWorkSeconds: number;
  isAnswered: boolean;
  direction: string;
  webhookUpdatedAt: string | null;
  // Call type flags for tags
  isTestCall: boolean;
  isVoicemail: boolean;
  isAbandoned: boolean;
  isMissed: boolean;
  ani: string | null;
  agentName: string | null;
}

interface WebhookCallsResponse {
  success: boolean;
  data: {
    records: WebhookCallRecord[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
    statistics?: {
      totalCalls: number;
      totalAnswered: number;
      totalMinutes: number;
      totalBillableMinutes: number;
      // Total duration for ALL calls (including test calls)
      totalDurationMinutesAll?: number;
      // Test call metrics
      totalTestCalls?: number;
      totalTestAnsweredCalls?: number;
      totalTestMinutes?: number;
      // Inbound breakdown
      inboundCalls?: number;
      inboundCallsReceived?: number;
      inboundAnswered?: number;
      inboundBillableMinutes?: number;
      // Outbound breakdown
      outboundCalls?: number;
      outboundCallsReceived?: number;
      outboundAnswered?: number;
      outboundBillableMinutes?: number;
      outboundConnectedBillableMinutes?: number;
      outboundAbandonedWaitMinutes?: number;
      // Call type counts
      voicemailCalls?: number;
      voicemailBillableMinutes?: number;
      abandonedCalls?: number;
      missedCalls?: number;
      // Direction-split missed/abandoned
      inboundMissed?: number;
      outboundMissed?: number;
      inboundAbandoned?: number;
      outboundAbandoned?: number;
      // Callback requests
      callbackCalls?: number;
      callbackMissedCalls?: number;
      callbackAbandonedCalls?: number;
      voicemailMissedOverlap?: number;
      // AHT and per-call averages (inbound answered calls only)
      ahtMinutes?: number;
      avgAcwSeconds?: number;
      avgInCallMinutes?: number;
    };
    dateRange: {
      startDate: string;
      endDate: string;
    };
  };
  error?: string;
}

interface CustomerProfile {
  id: string;
  name: string;
  genesysQueueNames: string[];
  queueKeys: string[];
  reportTimezone?: string | null;
}

interface CustomerSelectionState {
  [customerId: string]: boolean;
}

// ─── AI Minutes types ──────────────────────────────────────────────────────────

interface AiCallRecord {
  callId: string;
  startedAt: Date;
  phone: string;
  name: string;
  idNumber: string;
  calledFor: string;
  department: string;
  message: string;
  company: string;
  status: string;
  durationMs: number;
  agentName: string;
  direction: string;
}

interface AiCustomer {
  id: string;
  name: string;
  serviceType: string;
  agentId: string;
  agentName: string;
  calls: AiCallRecord[];
  totalCalls: number;
  totalMinutes: number;
  avgInCallMinutes: number;
  inboundCalls: number;
  inboundAnswered: number;
  inboundMinutes: number;
  outboundCalls: number;
  outboundAnswered: number;
  outboundMinutes: number;
}

/**
 * Format decimal minutes to "X mins Y secs" format
 * @param decimalMinutes - Minutes as decimal (e.g., 3.77)
 * @returns Formatted string (e.g., "3 mins 46 secs")
 */
function formatBillableMinutes(decimalMinutes: number): string {
  if (decimalMinutes === 0) return "0.00 mins";
  return `${decimalMinutes.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} mins`;
}

export function MinutesUsageWebhooks() {
  const [activeTab, setActiveTab] = useState<"aircall" | "ai">("aircall");

  // Date/time range state - default to today
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [startDate, setStartDate] = useState(format(today, "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(today, "yyyy-MM-dd"));
  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("23:59");

  // Customer selection state
  const [customers, setCustomers] = useState<CustomerProfile[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<CustomerSelectionState>({});
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  // Data state
  const [records, setRecords] = useState<WebhookCallRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });
  const [statistics, setStatistics] = useState({
    totalCalls: 0,
    totalAnswered: 0,
    totalMinutes: 0,
    totalBillableMinutes: 0,
    // Total duration for ALL calls (including test calls)
    totalDurationMinutesAll: 0,
    // Test call metrics
    totalTestCalls: 0,
    totalTestAnsweredCalls: 0,
    totalTestMinutes: 0,
    // Inbound breakdown
    inboundCalls: 0,
    inboundCallsReceived: 0,
    inboundAnswered: 0,
    inboundBillableMinutes: 0,
    // Outbound breakdown
    outboundCalls: 0,
    outboundCallsReceived: 0,
    outboundAnswered: 0,
    outboundBillableMinutes: 0,
    outboundConnectedBillableMinutes: 0,
    outboundAbandonedWaitMinutes: 0,
    // Call type counts
    voicemailCalls: 0,
    voicemailBillableMinutes: 0,
    abandonedCalls: 0,
    missedCalls: 0,
    // Direction-split missed/abandoned
    inboundMissed: 0,
    outboundMissed: 0,
    inboundAbandoned: 0,
    outboundAbandoned: 0,
    // Callback requests
    callbackCalls: 0,
    callbackMissedCalls: 0,
    callbackAbandonedCalls: 0,
    voicemailMissedOverlap: 0,
    // AHT and per-call averages (inbound answered calls only)
    ahtMinutes: 0,
    avgAcwSeconds: 0,
    avgInCallMinutes: 0,
  });

  // Cache key for localStorage
  const CUSTOMERS_CACHE_KEY = "minutes_usage_customers_cache";
  const CUSTOMERS_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

  // Load customers from cache first, then fetch fresh
  useEffect(() => {
    loadCustomersWithCache();
  }, []);

  const loadCustomersWithCache = async () => {
    try {
      const cached = localStorage.getItem(CUSTOMERS_CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const age = Date.now() - timestamp;
        // Invalidate cache if entries are missing queueKeys
        const isValid =
          age < CUSTOMERS_CACHE_TTL &&
          Array.isArray(data) &&
          data.length > 0 &&
          data.every((c: any) => Array.isArray(c.queueKeys));
        if (isValid) {
          setCustomers(data);
          return;
        }
      }
    } catch (e) {
      console.warn("Failed to load customers from cache:", e);
    }
    await fetchCustomers();
  };

  const fetchCustomers = async () => {
    try {
      const response = await fetch("/api/v1/customer-profiles?isActive=true&limit=500", {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });
      if (!response.ok) return;
      const data = await response.json();
      const list: CustomerProfile[] = (Array.isArray(data.data) ? data.data : []).map((p: any) => {
        const genesysQueueNames: string[] = Array.isArray(p.genesysQueueNames) ? p.genesysQueueNames : [];
        const telephonyQueueKeys: string[] = Array.isArray(p.telephonyQueueKeys) ? p.telephonyQueueKeys : [];
        // Same fallback as backend: telephonyQueueKeys first, then genesysQueueNames
        const queueKeys = telephonyQueueKeys.length > 0 ? telephonyQueueKeys : genesysQueueNames;
        return { id: p.id, name: p.qboDisplayName, genesysQueueNames, queueKeys, reportTimezone: p.reportTimezone || null };
      });
      setCustomers(list);
      localStorage.setItem(CUSTOMERS_CACHE_KEY, JSON.stringify({
        data: list,
        timestamp: Date.now(),
      }));
    } catch (e) {
      console.warn("Failed to fetch customers:", e);
    }
  };

  // Filter customers based on search
  const filteredCustomers = customers.filter((c) =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Selection helpers
  const selectedCount = Object.values(selectedCustomers).filter(Boolean).length;

  const handleSelectAll = () => {
    const newSelection: CustomerSelectionState = {};
    customers.forEach((c) => { newSelection[c.id] = true; });
    setSelectedCustomers(newSelection);
  };

  const handleSelectNone = () => {
    setSelectedCustomers({});
  };

  const handleToggleCustomer = (customerId: string) => {
    setSelectedCustomers((prev) => ({
      ...prev,
      [customerId]: !prev[customerId],
    }));
  };

  // Get selected customer IDs
  const getSelectedCustomerIds = useCallback(() => {
    return customers.filter((c) => selectedCustomers[c.id]).map((c) => c.id);
  }, [customers, selectedCustomers]);

  // Fetch webhook calls
  const fetchWebhookCalls = async (page: number = 1) => {
    setLoading(true);
    try {
      const selectedCustomerIds = getSelectedCustomerIds();

      // Combine date and time in Pacific timezone for consistent results
      const startDateTime = zonedTimeToUtc(`${startDate}T${startTime}:00`, DEFAULT_TIMEZONE);
      const endDateTime = zonedTimeToUtc(`${endDate}T${endTime}:59`, DEFAULT_TIMEZONE);

      const params = new URLSearchParams({
        startDate: startDateTime.toISOString(),
        endDate: endDateTime.toISOString(),
        page: page.toString(),
        limit: pagination.limit.toString(),
      });

      // Add customer IDs if any are selected
      if (selectedCustomerIds.length > 0) {
        selectedCustomerIds.forEach((id) => {
          params.append("customerIds", id);
        });
      }

      const response = await fetch(`/api/v1/aircall/webhook-calls?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch webhook calls");
      }

      const data: WebhookCallsResponse = await response.json();

      if (data.success && data.data) {
        setRecords(data.data.records);
        setPagination(data.data.pagination);
        if (data.data.statistics) {
          // Ensure all optional fields have default values
          setStatistics({
            totalCalls: data.data.statistics.totalCalls || 0,
            totalAnswered: data.data.statistics.totalAnswered || 0,
            totalMinutes: data.data.statistics.totalMinutes || 0,
            totalBillableMinutes: data.data.statistics.totalBillableMinutes || 0,
            totalDurationMinutesAll: data.data.statistics.totalDurationMinutesAll || 0,
            totalTestCalls: data.data.statistics.totalTestCalls || 0,
            totalTestAnsweredCalls: data.data.statistics.totalTestAnsweredCalls || 0,
            totalTestMinutes: data.data.statistics.totalTestMinutes || 0,
            // Inbound breakdown
            inboundCalls: data.data.statistics.inboundCalls || 0,
            inboundCallsReceived: data.data.statistics.inboundCallsReceived || 0,
            inboundAnswered: data.data.statistics.inboundAnswered || 0,
            inboundBillableMinutes: data.data.statistics.inboundBillableMinutes || 0,
            // Outbound breakdown
            outboundCalls: data.data.statistics.outboundCalls || 0,
            outboundCallsReceived: data.data.statistics.outboundCallsReceived || 0,
            outboundAnswered: data.data.statistics.outboundAnswered || 0,
            outboundBillableMinutes: data.data.statistics.outboundBillableMinutes || 0,
            outboundConnectedBillableMinutes: data.data.statistics.outboundConnectedBillableMinutes || 0,
            outboundAbandonedWaitMinutes: data.data.statistics.outboundAbandonedWaitMinutes || 0,
            // Call type counts
            voicemailCalls: data.data.statistics.voicemailCalls || 0,
            voicemailBillableMinutes: data.data.statistics.voicemailBillableMinutes || 0,
            abandonedCalls: data.data.statistics.abandonedCalls || 0,
            missedCalls: data.data.statistics.missedCalls || 0,
            // Direction-split missed/abandoned
            inboundMissed: data.data.statistics.inboundMissed || 0,
            outboundMissed: data.data.statistics.outboundMissed || 0,
            inboundAbandoned: data.data.statistics.inboundAbandoned || 0,
            outboundAbandoned: data.data.statistics.outboundAbandoned || 0,
            callbackCalls: data.data.statistics.callbackCalls || 0,
            callbackMissedCalls: data.data.statistics.callbackMissedCalls || 0,
            callbackAbandonedCalls: data.data.statistics.callbackAbandonedCalls || 0,
            voicemailMissedOverlap: data.data.statistics.voicemailMissedOverlap || 0,
            ahtMinutes: data.data.statistics.ahtMinutes || 0,
            avgAcwSeconds: data.data.statistics.avgAcwSeconds || 0,
            avgInCallMinutes: data.data.statistics.avgInCallMinutes || 0,
          });
        }
      } else {
        throw new Error(data.error || "Failed to fetch webhook calls");
      }
    } catch (error) {
      console.error("Error fetching webhook calls:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to fetch webhook calls"
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount and when dates/times or selected customers change
  useEffect(() => {
    fetchWebhookCalls(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, startTime, endTime, selectedCustomers]);

  // ── AI Minutes state ─────────────────────────────────────────────────────────
  const [aiCustomers, setAiCustomers] = useState<AiCustomer[]>([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiExpanded, setAiExpanded] = useState<Set<string>>(new Set());
  const [aiSelectedCustomers, setAiSelectedCustomers] = useState<Record<string, boolean>>({});
  const [aiDropdownOpen, setAiDropdownOpen] = useState(false);
  const [aiSearchQuery, setAiSearchQuery] = useState("");
  const aiDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (aiDropdownRef.current && !aiDropdownRef.current.contains(e.target as Node)) {
        setAiDropdownOpen(false);
      }
    };
    if (aiDropdownOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [aiDropdownOpen]);

  const fetchAiMinutes = useCallback(async () => {
    setAiLoading(true);
    try {
      const startDateTime = zonedTimeToUtc(`${startDate}T${startTime}:00`, DEFAULT_TIMEZONE);
      const endDateTime   = zonedTimeToUtc(`${endDate}T${endTime}:59`, DEFAULT_TIMEZONE);
      const params = new URLSearchParams({
        startDate: startDateTime.toISOString(),
        endDate:   endDateTime.toISOString(),
      });
      const res = await fetch(`/api/v1/retell/ai-minutes?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error ?? "Failed to fetch AI minutes");
      setAiCustomers(json.data.customers ?? []);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to fetch AI minutes");
    } finally {
      setAiLoading(false);
    }
  }, [startDate, endDate, startTime, endTime]);

  useEffect(() => {
    if (activeTab === "ai") fetchAiMinutes();
  }, [activeTab, fetchAiMinutes]);

  const toggleAiExpanded = (id: string) =>
    setAiExpanded((prev) => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  // Export Excel for the AI tab — mirrors the Aircall tab's downloadExcel, but all data is
  // already loaded client-side from /api/v1/retell/ai-minutes, so no fetch/pagination is needed.
  const [downloadingAi, setDownloadingAi] = useState(false);

  const downloadAiExcel = () => {
    if (aiCustomers.length === 0) {
      toast.error("No data to download");
      return;
    }

    setDownloadingAi(true);
    try {
      const selectedCount = Object.values(aiSelectedCustomers).filter(Boolean).length;
      const customersToExport = selectedCount === 0
        ? aiCustomers
        : aiCustomers.filter((c) => aiSelectedCustomers[c.id]);

      const wb = XLSX.utils.book_new();
      const filename = `ai-minutes-usage_${startDate}_to_${endDate}.xlsx`;

      const COL_WIDTHS = [
        { wch: 18 }, { wch: 12 }, { wch: 20 }, { wch: 16 }, { wch: 16 }, { wch: 14 },
      ];
      const NCOLS = COL_WIDTHS.length;
      const empty = () => Array(NCOLS).fill("");

      const aoa: any[][] = [];
      const merges: XLSX.Range[] = [];
      const summaryRows: { customer: AiCustomer }[] = [];

      const addMergedRow = (text: string) => {
        const ri = aoa.length;
        aoa.push([text, ...Array(NCOLS - 1).fill("")]);
        merges.push({ s: { r: ri, c: 0 }, e: { r: ri, c: NCOLS - 1 } });
      };

      aoa.push(empty());

      for (const customer of customersToExport) {
        addMergedRow(
          `  ${customer.name.toUpperCase()} (${customer.serviceType === "ai_medical" ? "AI Medical" : "AI Admin"})  |  ${customer.totalCalls} calls  |  ${formatBillableMinutes(customer.totalMinutes)}`
        );

        if (customer.calls.length === 0) {
          addMergedRow("  No calls found for this customer in the selected date range.");
        } else {
          aoa.push(["Time", "Direction", "Caller", "Called For", "Department", "Duration (min)"]);
          for (const call of customer.calls) {
            aoa.push([
              format(new Date(call.startedAt), "MMM d, yyyy h:mm a"),
              call.direction === "inbound" ? "Inbound" : "Outbound",
              call.name || call.phone || "—",
              call.calledFor ? call.calledFor.charAt(0).toUpperCase() + call.calledFor.slice(1) : "—",
              call.department || "—",
              Number((call.durationMs / 60000).toFixed(2)),
            ]);
          }
          addMergedRow(
            `  Subtotal — ${customer.name}: ${customer.totalCalls} calls  |  Inbound answered: ${customer.inboundAnswered}  |  Outbound answered: ${customer.outboundAnswered}  |  Billable: ${formatBillableMinutes(customer.totalMinutes)}`
          );
        }

        aoa.push(empty());
        aoa.push(empty());
        summaryRows.push({ customer });
      }

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!merges"] = merges;
      ws["!cols"] = COL_WIDTHS;
      XLSX.utils.book_append_sheet(wb, ws, "Call Records");

      // Summary sheet
      const summaryAoa: any[][] = [];
      const summaryMerges: XLSX.Range[] = [];
      const SCOLS = 5;
      const addSummaryMerge = (text: string) => {
        const ri = summaryAoa.length;
        summaryAoa.push([text, "", "", "", ""]);
        summaryMerges.push({ s: { r: ri, c: 0 }, e: { r: ri, c: SCOLS - 1 } });
      };

      addSummaryMerge(`AI Minutes Usage Summary  —  ${startDate}  to  ${endDate}`);
      summaryAoa.push(["", "", "", "", ""]);
      summaryAoa.push(["Customer", "Total Calls", "Inbound Answered", "Outbound Answered", "Billable Mins"]);

      let grandCalls = 0, grandInbound = 0, grandOutbound = 0, grandMinutes = 0;
      for (const { customer } of summaryRows) {
        summaryAoa.push([
          customer.name,
          customer.totalCalls,
          customer.inboundAnswered,
          customer.outboundAnswered,
          customer.totalMinutes.toFixed(2),
        ]);
        grandCalls += customer.totalCalls;
        grandInbound += customer.inboundAnswered;
        grandOutbound += customer.outboundAnswered;
        grandMinutes += customer.totalMinutes;
      }
      summaryAoa.push(["", "", "", "", ""]);
      summaryAoa.push(["GRAND TOTAL", grandCalls, grandInbound, grandOutbound, grandMinutes.toFixed(2)]);

      const summaryWs = XLSX.utils.aoa_to_sheet(summaryAoa);
      summaryWs["!merges"] = summaryMerges;
      summaryWs["!cols"] = [{ wch: 32 }, { wch: 14 }, { wch: 16 }, { wch: 16 }, { wch: 14 }];
      XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");

      XLSX.writeFile(wb, filename);
      toast.success(`Downloaded ${customersToExport.reduce((s, c) => s + c.totalCalls, 0)} calls`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Download failed");
    } finally {
      setDownloadingAi(false);
    }
  };

  // Download Excel function - fetches ALL records (not just current page)
  const [downloading, setDownloading] = useState(false);
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [sendEmailReportType, setSendEmailReportType] = useState<"minutes_usage" | "ai_minutes_usage">("minutes_usage");
  const canSendEmailReport = useAuthStore(
    (s) => s.user?.permissions?.includes(ACTION_KEYS.EMAIL_REPORT_SEND) ?? false
  );

  const downloadExcel = async () => {
    if (pagination.total === 0) {
      toast.error("No data to download");
      return;
    }

    setDownloading(true);
    toast.loading("Fetching all records...", { id: "download" });

    try {
      const selectedCustomerIds = getSelectedCustomerIds();
      const selectedCustomerList = customers.filter((c) => selectedCustomers[c.id]);

      const startDateTime = zonedTimeToUtc(`${startDate}T${startTime}:00`, DEFAULT_TIMEZONE);
      const endDateTime = zonedTimeToUtc(`${endDate}T${endTime}:59`, DEFAULT_TIMEZONE);

      // Paginate through all records
      const PAGE_SIZE = 1000;
      let currentPage = 1;
      let totalPages = 1;
      const allRecords: WebhookCallRecord[] = [];

      do {
        const params = new URLSearchParams({
          startDate: startDateTime.toISOString(),
          endDate: endDateTime.toISOString(),
          page: String(currentPage),
          limit: String(PAGE_SIZE),
        });
        selectedCustomerIds.forEach((id) => params.append("customerIds", id));

        const response = await fetch(`/api/v1/aircall/webhook-calls?${params}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
        });
        if (!response.ok) throw new Error("Failed to fetch records for download");

        const data: WebhookCallsResponse = await response.json();
        if (!data.success || !data.data?.records) throw new Error(data.error || "Failed to fetch records");

        allRecords.push(...data.data.records);
        totalPages = data.data.pagination.totalPages;

        if (totalPages > 1) {
          toast.loading(`Fetching records... (${allRecords.length} / ${data.data.pagination.total})`, { id: "download" });
        }
        currentPage++;
      } while (currentPage <= totalPages);

      const wb = XLSX.utils.book_new();
      const filename = `call-records_${startDate}_to_${endDate}.xlsx`;

      // ── COLUMN DEFINITIONS ──────────────────────────────────────────────
      const COL_WIDTHS = [
        { wch: 14 }, { wch: 32 }, { wch: 22 }, { wch: 22 },
        { wch: 22 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 12 },
      ];
      const NCOLS = COL_WIDTHS.length;
      const empty = () => Array(NCOLS).fill("");

      // Build column headers using the customer's timezone abbreviation
      const makeColHeaders = (tz: string | null | undefined): string[] => {
        const label = tzLabel(tz);
        return [
          "Call ID", "Queue",
          `Answered At (${label})`, `Ended At (${label})`, `Transfer At (${label})`,
          "Duration (sec)", "Billable Mins", "Status", "Direction",
        ];
      };

      // Build a data row, formatting timestamps in the customer's timezone
      const makeRecordRow = (r: WebhookCallRecord, tz: string | null | undefined): any[] => [
        r.callId,
        r.queueName || r.queueId || "N/A",
        r.answeredAt ? formatInTimezone(r.answeredAt, tz) : "—",
        formatInTimezone(r.endedAt, tz),
        r.externalTransferredAt ? formatInTimezone(r.externalTransferredAt, tz) : "—",
        r.durationSeconds,
        Number(r.billableMinutes.toFixed(2)),
        r.isAnswered ? "Answered" : "Not Answered",
        r.direction,
      ];

      // ── GROUPED BY CUSTOMER (when customers are selected) ────────────────
      if (selectedCustomerList.length > 0) {
        // Match each record to a customer using the same queue keys the backend used
        // (telephonyQueueKeys if set, otherwise genesysQueueNames — via queueKeys field)
        const buckets = new Map<string, WebhookCallRecord[]>(
          selectedCustomerList.map((c) => [c.id, []])
        );
        const unmatched: WebhookCallRecord[] = [];

        for (const record of allRecords) {
          const rQueue = (record.queueName || "").toLowerCase();

          // 1. Exact match against configured queue keys (telephonyQueueKeys || genesysQueueNames)
          let match = selectedCustomerList.find((c) =>
            (c.queueKeys ?? c.genesysQueueNames ?? []).some((q) => q.toLowerCase() === rQueue)
          );

          // 2. If only one customer selected, all records belong to them
          if (!match && selectedCustomerList.length === 1) {
            match = selectedCustomerList[0];
          }

          // 3. Fallback: customer name appears in the queue name (e.g. "Master Eye Associates Abilene" → "Master Eye Associates")
          if (!match) {
            match = selectedCustomerList.find((c) =>
              rQueue.includes(c.name.toLowerCase())
            );
          }

          if (match) {
            buckets.get(match.id)!.push(record);
          } else {
            unmatched.push(record);
          }
        }

        // Build the grouped worksheet (array-of-arrays for precise control)
        const aoa: any[][] = [];
        const merges: XLSX.Range[] = [];
        const summaryRows: any[] = [];

        const addMergedRow = (text: string) => {
          const ri = aoa.length;
          aoa.push([text, ...Array(NCOLS - 1).fill("")]);
          merges.push({ s: { r: ri, c: 0 }, e: { r: ri, c: NCOLS - 1 } });
        };

        // Opening blank row
        aoa.push(empty());

        const msToMins = (ms: number) => Math.round((ms / 60000) * 100) / 100;

        for (const customer of selectedCustomerList) {
          const recs = buckets.get(customer.id) || [];
          const totalBillable = msToMins(recs.reduce((s, r) => s + r.billableMilliseconds, 0));
          const answeredCount = recs.filter((r) => r.isAnswered).length;
          const answeredPct = recs.length > 0 ? Math.round((answeredCount / recs.length) * 100) : 0;

          // Direction breakdown
          const inboundRecs = recs.filter((r) => r.direction === "inbound");
          const outboundRecs = recs.filter((r) => r.direction !== "inbound");
          const inboundBillable = msToMins(inboundRecs.reduce((s, r) => s + r.billableMilliseconds, 0));
          const outboundBillable = msToMins(outboundRecs.reduce((s, r) => s + r.billableMilliseconds, 0));

          // ── Customer header
          addMergedRow(`  CUSTOMER: ${customer.name.toUpperCase()}  |  ${recs.length} call${recs.length !== 1 ? "s" : ""}  |  ${answeredCount} answered (${answeredPct}%)  |  ${totalBillable.toFixed(2)} billable mins`);

          if (recs.length === 0) {
            addMergedRow("  No records found for this customer in the selected date range.");
          } else {
            // Column headers — use this customer's timezone
            aoa.push(makeColHeaders(customer.reportTimezone));

            // Data rows — format timestamps in this customer's timezone
            for (const r of recs) {
              aoa.push(makeRecordRow(r, customer.reportTimezone));
            }

            // Subtotal row
            addMergedRow(`  Subtotal — ${customer.name}: ${recs.length} records  |  Answered: ${answeredCount} / ${recs.length}  |  Billable: ${totalBillable.toFixed(2)} mins`);
            // Direction breakdown rows
            addMergedRow(`  ↳ Inbound: ${inboundRecs.length} calls  |  Billable: ${inboundBillable.toFixed(2)} mins`);
            addMergedRow(`  ↳ Outbound: ${outboundRecs.length} calls  |  Billable: ${outboundBillable.toFixed(2)} mins`);
          }

          // Separator
          aoa.push(empty());
          aoa.push(empty());

          // Collect per-queue breakdown for summary sheet
          const queueMap = new Map<string, { calls: number; answered: number; billableMs: number }>();
          for (const r of recs) {
            const qn = r.queueName || r.queueId || "Unknown Queue";
            const existing = queueMap.get(qn) ?? { calls: 0, answered: 0, billableMs: 0 };
            queueMap.set(qn, {
              calls: existing.calls + 1,
              answered: existing.answered + (r.isAnswered ? 1 : 0),
              billableMs: existing.billableMs + r.billableMilliseconds,
            });
          }
          summaryRows.push({
            customer, queueMap,
            totalCalls: recs.length, totalAnswered: answeredCount,
            totalBillable,
            totalBillableMs: recs.reduce((s: number, r: WebhookCallRecord) => s + r.billableMilliseconds, 0),
          });
        }

        // Unmatched records section (if any)
        if (unmatched.length > 0) {
          const unmatchedBillable = msToMins(unmatched.reduce((s, r) => s + r.billableMilliseconds, 0));
          addMergedRow(`  OTHER / UNMATCHED QUEUES  |  ${unmatched.length} calls  |  ${unmatchedBillable.toFixed(2)} billable mins`);
          aoa.push(makeColHeaders(null));
          for (const r of unmatched) aoa.push(makeRecordRow(r, null));
          aoa.push(empty());
        }

        // Build Call Records worksheet
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws["!merges"] = merges;
        ws["!cols"] = COL_WIDTHS;
        XLSX.utils.book_append_sheet(wb, ws, "Call Records");

        // ── Summary sheet — Customer > Queues > Customer Total > Grand Total ──
        const summaryAoa: any[][] = [];
        const summaryMerges: XLSX.Range[] = [];
        const SCOLS = 5;

        const addSummaryMerge = (text: string) => {
          const ri = summaryAoa.length;
          summaryAoa.push([text, "", "", "", ""]);
          summaryMerges.push({ s: { r: ri, c: 0 }, e: { r: ri, c: SCOLS - 1 } });
        };

        // Title
        addSummaryMerge(`Call Records Summary  —  ${startDate}  to  ${endDate}`);
        summaryAoa.push(["", "", "", "", ""]);

        let grandCalls = 0;
        let grandAnswered = 0;
        let grandBillableMs = 0;

        for (const row of summaryRows) {
          const { customer, queueMap, totalCalls, totalAnswered, totalBillable } = row as any;

          // Customer header
          addSummaryMerge(`CUSTOMER: ${customer.name}`);

          // Column headers for this section
          summaryAoa.push(["Queue", "Total Calls", "Answered", "Answer Rate", "Billable Mins"]);

          // One row per queue
          const sortedQueues = [...queueMap.entries()].sort((a, b) => a[0].localeCompare(b[0]));
          for (const [qName, qStats] of sortedQueues) {
            const qPct = qStats.calls > 0 ? Math.round((qStats.answered / qStats.calls) * 100) : 0;
            summaryAoa.push([
              `  ${qName}`,
              qStats.calls,
              qStats.answered,
              `${qPct}%`,
              msToMins(qStats.billableMs).toFixed(2),
            ]);
          }

          // Customer total row
          const custPct = totalCalls > 0 ? Math.round((totalAnswered / totalCalls) * 100) : 0;
          summaryAoa.push([
            `${customer.name} — Total`,
            totalCalls,
            totalAnswered,
            `${custPct}%`,
            totalBillable.toFixed(2),
          ]);

          // Blank separator
          summaryAoa.push(["", "", "", "", ""]);

          grandCalls      += totalCalls;
          grandAnswered   += totalAnswered;
          grandBillableMs += (row as any).totalBillableMs ?? 0;
        }

        // Grand total
        summaryAoa.push(["", "", "", "", ""]);
        const grandPct = grandCalls > 0 ? Math.round((grandAnswered / grandCalls) * 100) : 0;
        summaryAoa.push([
          "GRAND TOTAL",
          grandCalls,
          grandAnswered,
          `${grandPct}%`,
          msToMins(grandBillableMs).toFixed(2),
        ]);

        const summaryWs = XLSX.utils.aoa_to_sheet(summaryAoa);
        summaryWs["!merges"] = summaryMerges;
        summaryWs["!cols"] = [{ wch: 38 }, { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 16 }];
        XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");

      } else {
        // ── FLAT EXPORT (no customers selected) ─────────────────────────────
        const flatTzLabel = tzLabel(null); // PT when no customer selected
        const excelData = allRecords.map((r) => ({
          "Call ID": r.callId,
          "Queue": r.queueName || r.queueId || "N/A",
          [`Answered At (${flatTzLabel})`]: r.answeredAt ? formatInTimezone(r.answeredAt, null) : "—",
          [`Ended At (${flatTzLabel})`]: formatInTimezone(r.endedAt, null),
          [`Transfer At (${flatTzLabel})`]: r.externalTransferredAt ? formatInTimezone(r.externalTransferredAt, null) : "—",
          "Duration (sec)": r.durationSeconds,
          "Billable Minutes": r.billableMinutes.toFixed(2),
          "Status": r.isAnswered ? "Answered" : "Not Answered",
          "Direction": r.direction,
        }));
        const ws = XLSX.utils.json_to_sheet(excelData);
        ws["!cols"] = COL_WIDTHS;
        XLSX.utils.book_append_sheet(wb, ws, "Call Records");

        const summaryWs = XLSX.utils.json_to_sheet([
          { "Metric": "Total Calls",            "Value": statistics.totalCalls },
          { "Metric": "Answered Calls",          "Value": statistics.totalAnswered },
          { "Metric": "Total Duration (min)",    "Value": statistics.totalDurationMinutesAll.toFixed(2) },
          { "Metric": "Billable Minutes",        "Value": statistics.totalBillableMinutes.toFixed(2) },
          { "Metric": "Test Calls",              "Value": statistics.totalTestCalls },
          { "Metric": "Test Minutes",            "Value": statistics.totalTestMinutes.toFixed(2) },
        ]);
        XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");
      }

      XLSX.writeFile(wb, filename);
      toast.success(`Downloaded ${allRecords.length} records`, { id: "download" });
    } catch (error) {
      console.error("Download error:", error);
      toast.error(error instanceof Error ? error.message : "Download failed", { id: "download" });
    } finally {
      setDownloading(false);
    }
  };

  const [downloadingSpecial, setDownloadingSpecial] = useState(false);

  const downloadSpecialReport = async () => {
    if (pagination.total === 0) {
      toast.error("No data to download");
      return;
    }

    setDownloadingSpecial(true);
    toast.loading("Building Special Report...", { id: "special-report" });

    try {
      const selectedCustomerIds = getSelectedCustomerIds();
      const startDateTime = zonedTimeToUtc(`${startDate}T${startTime}:00`, DEFAULT_TIMEZONE);
      const endDateTime   = zonedTimeToUtc(`${endDate}T${endTime}:59`, DEFAULT_TIMEZONE);

      const params = new URLSearchParams({
        startDate: startDateTime.toISOString(),
        endDate:   endDateTime.toISOString(),
      });
      selectedCustomerIds.forEach((id) => params.append("customerIds", id));

      const response = await fetch(`/api/v1/aircall/special-report?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });
      if (!response.ok) throw new Error("Failed to generate special report");

      const blob     = await response.blob();
      const url      = URL.createObjectURL(blob);
      const a        = document.createElement("a");
      a.href         = url;
      a.download     = `special-report_${startDate}_to_${endDate}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);

      toast.success("Special Report downloaded", { id: "special-report" });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Download failed", { id: "special-report" });
    } finally {
      setDownloadingSpecial(false);
    }
  };

  // Use statistics from API (totals across all records, not just current page)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Minutes Usage with Webhooks
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            View call records processed via webhooks with billable minutes
            calculation
          </p>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === "aircall" && (
            <>
              <button
                onClick={downloadExcel}
                disabled={loading || downloading || pagination.total === 0}
                className="flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
              >
                {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {downloading ? "Downloading..." : "Export Excel"}
              </button>
              <button
                onClick={downloadSpecialReport}
                disabled={loading || downloadingSpecial || pagination.total === 0}
                className="flex items-center gap-2 rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50"
              >
                {downloadingSpecial ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {downloadingSpecial ? "Building..." : "Special Report"}
              </button>
              {canSendEmailReport && (
                <button
                  onClick={() => { setSendEmailReportType("minutes_usage"); setShowSendEmailModal(true); }}
                  disabled={loading}
                  className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  <Mail className="h-4 w-4" />
                  Send to Email
                </button>
              )}
              <button
                onClick={() => fetchWebhookCalls(pagination.page)}
                disabled={loading}
                className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Refresh
              </button>
            </>
          )}
          {activeTab === "ai" && (
            <>
              <button
                onClick={downloadAiExcel}
                disabled={aiLoading || downloadingAi || aiCustomers.length === 0}
                className="flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
              >
                {downloadingAi ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {downloadingAi ? "Downloading..." : "Export Excel"}
              </button>
              {canSendEmailReport && (
                <button
                  onClick={() => { setSendEmailReportType("ai_minutes_usage"); setShowSendEmailModal(true); }}
                  disabled={aiLoading}
                  className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  <Mail className="h-4 w-4" />
                  Send to Email
                </button>
              )}
              <button
                onClick={fetchAiMinutes}
                disabled={aiLoading}
                className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Refresh
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 border-b border-gray-200">
        <button
          onClick={() => setActiveTab("aircall")}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "aircall"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          <PhoneIncoming className="h-4 w-4" />
          Aircall
        </button>
        <button
          onClick={() => setActiveTab("ai")}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "ai"
              ? "border-purple-600 text-purple-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          <Bot className="h-4 w-4" />
          AI Minutes
        </button>
      </div>

      {/* Date/Time Range Filters — shared across both tabs */}
      <div className="rounded-lg bg-white p-4 shadow">
        <div className="grid gap-4 md:grid-cols-4">
          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Start Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Time
            </label>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate}
              max={format(new Date(), "yyyy-MM-dd")}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* End Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Time
            </label>
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* ── AI Minutes tab content ────────────────────────────────────────────── */}
      {activeTab === "ai" && (() => {
        const aiSelectedCount = Object.values(aiSelectedCustomers).filter(Boolean).length;
        const visibleAiCustomers = aiSelectedCount === 0
          ? aiCustomers
          : aiCustomers.filter((c) => aiSelectedCustomers[c.id]);
        const filteredAiCustomers = aiCustomers.filter((c) =>
          c.name.toLowerCase().includes(aiSearchQuery.toLowerCase())
        );

        const aiStats = {
          totalCalls:      visibleAiCustomers.reduce((s, c) => s + c.totalCalls, 0),
          totalMinutes:    visibleAiCustomers.reduce((s, c) => s + c.totalMinutes, 0),
          inboundCalls:    visibleAiCustomers.reduce((s, c) => s + c.inboundCalls, 0),
          inboundAnswered: visibleAiCustomers.reduce((s, c) => s + c.inboundAnswered, 0),
          inboundMinutes:  visibleAiCustomers.reduce((s, c) => s + c.inboundMinutes, 0),
          outboundCalls:   visibleAiCustomers.reduce((s, c) => s + c.outboundCalls, 0),
          outboundAnswered:visibleAiCustomers.reduce((s, c) => s + c.outboundAnswered, 0),
          outboundMinutes: visibleAiCustomers.reduce((s, c) => s + c.outboundMinutes, 0),
        };
        const avgInCallMinutes = aiStats.totalCalls > 0 ? aiStats.totalMinutes / aiStats.totalCalls : 0;

        return (
          <div className="space-y-4">
            {/* Customer filter */}
            <div className="rounded-lg bg-white p-4 shadow space-y-4">
              <div className="relative" ref={aiDropdownRef}>
                <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Customer:</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setAiDropdownOpen(!aiDropdownOpen)}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">
                        {aiSelectedCount === 0 ? "All AI Customers" : `${aiSelectedCount} customer${aiSelectedCount > 1 ? "s" : ""} selected`}
                      </span>
                      <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${aiDropdownOpen ? "rotate-180" : ""}`} />
                    </div>
                  </button>
                  {aiDropdownOpen && (
                    <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-300 bg-white shadow-lg">
                      <div className="p-2">
                        <div className="mb-2 flex items-center gap-2 border-b border-gray-200 pb-2">
                          <Search className="h-4 w-4 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search customers..."
                            value={aiSearchQuery}
                            onChange={(e) => setAiSearchQuery(e.target.value)}
                            className="flex-1 border-0 px-2 py-1 text-sm focus:outline-none"
                          />
                          {aiSearchQuery && (
                            <button onClick={() => setAiSearchQuery("")} className="text-gray-400 hover:text-gray-600">
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        <div className="mb-2 flex gap-2 border-b border-gray-200 pb-2">
                          <button
                            onClick={() => { const s: Record<string, boolean> = {}; aiCustomers.forEach((c) => { s[c.id] = true; }); setAiSelectedCustomers(s); }}
                            className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700"
                          >
                            <CheckSquare className="h-3 w-3" /> Select All
                          </button>
                          <button
                            onClick={() => setAiSelectedCustomers({})}
                            className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-700"
                          >
                            <Square className="h-3 w-3" /> Select None
                          </button>
                        </div>
                        <div className="max-h-60 overflow-y-auto">
                          {filteredAiCustomers.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-gray-500">No customers found</div>
                          ) : filteredAiCustomers.map((c) => (
                            <label key={c.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={aiSelectedCustomers[c.id] || false}
                                onChange={() => setAiSelectedCustomers((prev) => ({ ...prev, [c.id]: !prev[c.id] }))}
                                className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                              />
                              <span className="text-sm text-gray-700">{c.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {aiSelectedCount > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {aiCustomers.filter((c) => aiSelectedCustomers[c.id]).map((c) => (
                      <span key={c.id} className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-800">
                        {c.name}
                        <button onClick={() => setAiSelectedCustomers((prev) => ({ ...prev, [c.id]: false }))} className="text-purple-600 hover:text-purple-800">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {aiLoading ? (
              <div className="rounded-lg bg-white shadow flex items-center justify-center py-16 gap-2 text-gray-500">
                <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                <span>Loading AI minutes...</span>
              </div>
            ) : aiCustomers.length === 0 ? (
              <div className="rounded-lg bg-white shadow flex flex-col items-center justify-center py-16 text-gray-400">
                <Bot className="h-12 w-12 mb-3" />
                <p className="text-sm">No AI calls found for the selected date range.</p>
              </div>
            ) : (
              <>
                {/* Top metrics cards */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="rounded-lg bg-white p-5 shadow border-l-4 border-purple-500">
                    <div className="flex items-center gap-3">
                      <Database className="h-7 w-7 text-purple-600 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-gray-500">Total Calls Answered</p>
                        <p className="text-2xl font-semibold text-gray-900">{aiStats.totalCalls.toLocaleString()}</p>
                        <p className="text-xs text-gray-400">all ended calls</p>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-lg bg-white p-5 shadow border-l-4 border-indigo-400">
                    <div className="flex items-center gap-3">
                      <Clock className="h-7 w-7 text-indigo-600 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-gray-500">Avg In-Call Time</p>
                        <p className="text-xl font-semibold text-gray-900">{formatBillableMinutes(avgInCallMinutes)}</p>
                        <p className="text-xs text-gray-400">per answered call</p>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-lg bg-white p-5 shadow border-l-4 border-green-400">
                    <div className="flex items-center gap-3">
                      <Clock className="h-7 w-7 text-green-600 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-gray-500">Billable Time</p>
                        <p className="text-xl font-semibold text-gray-900">{formatBillableMinutes(aiStats.totalMinutes)}</p>
                        <p className="text-xs text-gray-400">total call duration</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Inbound / Outbound breakdown */}
                <div className="rounded-lg bg-white shadow overflow-hidden">
                  <div className="px-5 pt-4 pb-5 border-b border-gray-100">
                    <div className="flex items-center gap-2 mb-3">
                      <PhoneIncoming className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-semibold text-blue-700 uppercase tracking-wide">Inbound</span>
                      <span className="ml-auto text-xs text-gray-400">{aiStats.inboundCalls.toLocaleString()} total inbound</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                      <div className="rounded-lg bg-blue-50 border-l-4 border-blue-500 p-3">
                        <p className="text-xs font-medium text-gray-500 mb-1">Total Inbound</p>
                        <p className="text-2xl font-semibold text-gray-900">{aiStats.inboundCalls.toLocaleString()}</p>
                      </div>
                      <div className="rounded-lg bg-green-50 border-l-4 border-green-500 p-3">
                        <p className="text-xs font-medium text-gray-500 mb-1">Answered</p>
                        <p className="text-2xl font-semibold text-gray-900">{aiStats.inboundAnswered.toLocaleString()}</p>
                        <p className="text-xs text-blue-600 mt-0.5">{formatBillableMinutes(aiStats.inboundMinutes)}</p>
                      </div>
                      <div className="rounded-lg bg-indigo-50 border-l-4 border-indigo-400 p-3">
                        <p className="text-xs font-medium text-gray-500 mb-1">Billable Time</p>
                        <p className="text-xl font-semibold text-gray-900">{formatBillableMinutes(aiStats.inboundMinutes)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="px-5 pt-4 pb-5">
                    <div className="flex items-center gap-2 mb-3">
                      <PhoneOutgoing className="h-4 w-4 text-purple-600" />
                      <span className="text-sm font-semibold text-purple-700 uppercase tracking-wide">Outbound</span>
                      <span className="ml-auto text-xs text-gray-400">{aiStats.outboundCalls.toLocaleString()} total outbound</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                      <div className="rounded-lg bg-purple-50 border-l-4 border-purple-500 p-3">
                        <p className="text-xs font-medium text-gray-500 mb-1">Total Outbound</p>
                        <p className="text-2xl font-semibold text-gray-900">{aiStats.outboundCalls.toLocaleString()}</p>
                      </div>
                      <div className="rounded-lg bg-green-50 border-l-4 border-green-400 p-3">
                        <p className="text-xs font-medium text-gray-500 mb-1">Connected</p>
                        <p className="text-2xl font-semibold text-gray-900">{aiStats.outboundAnswered.toLocaleString()}</p>
                        <p className="text-xs text-purple-600 mt-0.5">{formatBillableMinutes(aiStats.outboundMinutes)}</p>
                      </div>
                      <div className="rounded-lg bg-violet-50 border-l-4 border-violet-400 p-3">
                        <p className="text-xs font-medium text-gray-500 mb-1">Billable Time</p>
                        <p className="text-xl font-semibold text-gray-900">{formatBillableMinutes(aiStats.outboundMinutes)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Per-customer table */}
                <div className="rounded-lg bg-white shadow">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900">AI Customers</h2>
                    <span className="text-sm text-gray-500">{visibleAiCustomers.length} customer{visibleAiCustomers.length !== 1 ? "s" : ""}</span>
                  </div>
                  <table className="w-full text-sm divide-y divide-gray-100">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="w-8 px-4 py-3" />
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Service</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Calls Answered</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Inbound</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Outbound</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Avg In-Call</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Billable Time</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {visibleAiCustomers.map((c) => {
                        const expanded = aiExpanded.has(c.id);
                        return (
                          <>
                            <tr key={c.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => toggleAiExpanded(c.id)}>
                              <td className="px-4 py-3">
                                {expanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                              </td>
                              <td className="px-4 py-3 font-medium text-gray-900">{c.name}</td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                  c.serviceType === "ai_medical" ? "bg-blue-100 text-blue-800" : "bg-purple-100 text-purple-800"
                                }`}>
                                  <Bot className="h-3 w-3" />
                                  {c.serviceType === "ai_medical" ? "AI Medical" : "AI Admin"}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right font-semibold text-gray-900">{c.totalCalls.toLocaleString()}</td>
                              <td className="px-4 py-3 text-right text-blue-700">{c.inboundAnswered.toLocaleString()}</td>
                              <td className="px-4 py-3 text-right text-purple-700">{c.outboundAnswered.toLocaleString()}</td>
                              <td className="px-4 py-3 text-right text-gray-700">{formatBillableMinutes(c.avgInCallMinutes)}</td>
                              <td className="px-4 py-3 text-right font-semibold text-green-700">{formatBillableMinutes(c.totalMinutes)}</td>
                            </tr>
                            {expanded && (
                              <tr key={`${c.id}-calls`}>
                                <td colSpan={8} className="p-0 bg-gray-50 border-t border-b border-gray-200">
                                  <div className="overflow-x-auto">
                                    <table className="min-w-full text-xs divide-y divide-gray-200">
                                      <thead className="bg-gray-100">
                                        <tr>
                                          <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Time</th>
                                          <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Direction</th>
                                          <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Caller</th>
                                          <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Called For</th>
                                          <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Department</th>
                                          <th className="px-4 py-2 text-right font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                                        </tr>
                                      </thead>
                                      <tbody className="bg-white divide-y divide-gray-100">
                                        {c.calls.map((call) => (
                                          <tr key={call.callId} className="hover:bg-gray-50">
                                            <td className="px-4 py-2 text-gray-600 whitespace-nowrap">
                                              {format(new Date(call.startedAt), "MMM d, h:mm a")}
                                            </td>
                                            <td className="px-4 py-2">
                                              {call.direction === "inbound" ? (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                                                  <PhoneIncoming className="h-3 w-3" /> Inbound
                                                </span>
                                              ) : (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
                                                  <PhoneOutgoing className="h-3 w-3" /> Outbound
                                                </span>
                                              )}
                                            </td>
                                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">
                                              {call.name || call.phone || <span className="text-gray-300">—</span>}
                                            </td>
                                            <td className="px-4 py-2 text-gray-600">
                                              {call.calledFor ? call.calledFor.charAt(0).toUpperCase() + call.calledFor.slice(1) : <span className="text-gray-300">—</span>}
                                            </td>
                                            <td className="px-4 py-2 text-gray-600">
                                              {call.department || <span className="text-gray-300">—</span>}
                                            </td>
                                            <td className="px-4 py-2 text-right text-gray-700 tabular-nums">
                                              {formatBillableMinutes(call.durationMs / 60000)}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* ── Aircall tab content ───────────────────────────────────────────────── */}
      {activeTab === "aircall" && <>

      {/* Customer Selector */}
      <div className="rounded-lg bg-white p-4 shadow space-y-4">
        <div className="relative" ref={dropdownRef}>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filter by Customer:
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <div className="flex items-center justify-between">
                <span className="text-gray-700">
                  {selectedCount === 0
                    ? "All Customers"
                    : `${selectedCount} customer${selectedCount > 1 ? "s" : ""} selected`}
                </span>
                <ChevronDown
                  className={`h-5 w-5 text-gray-400 transition-transform ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </div>
            </button>

            {dropdownOpen && (
              <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-300 bg-white shadow-lg">
                <div className="p-2">
                  <div className="mb-2 flex items-center gap-2 border-b border-gray-200 pb-2">
                    <Search className="h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search customers..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="flex-1 border-0 px-2 py-1 text-sm focus:outline-none"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery("")}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <div className="mb-2 flex gap-2 border-b border-gray-200 pb-2">
                    <button
                      onClick={handleSelectAll}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                    >
                      <CheckSquare className="h-3 w-3" />
                      Select All
                    </button>
                    <button
                      onClick={handleSelectNone}
                      className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-700"
                    >
                      <Square className="h-3 w-3" />
                      Select None
                    </button>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {filteredCustomers.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500">
                        No customers found
                      </div>
                    ) : (
                      filteredCustomers.map((customer) => (
                        <label
                          key={customer.id}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedCustomers[customer.id] || false}
                            onChange={() => handleToggleCustomer(customer.id)}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">
                            {customer.name}
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          {selectedCount > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {customers
                .filter((c) => selectedCustomers[c.id])
                .map((customer) => (
                  <span
                    key={customer.id}
                    className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800"
                  >
                    {customer.name}
                    <button
                      onClick={() => handleToggleCustomer(customer.id)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards - Row 1: Main metrics (core-definitions billable totals) */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <div className="rounded-lg bg-white p-5 shadow">
          <div className="flex items-center gap-3">
            <Database className="h-7 w-7 text-blue-600 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-gray-500">Total Billable Calls</p>
              <p className="text-2xl font-semibold text-gray-900">
                {(statistics.inboundAnswered + statistics.voicemailCalls + statistics.outboundAnswered + statistics.outboundAbandoned).toLocaleString()}
              </p>
              <p className="text-xs text-gray-400">Inbound Answered + VMs + Outbound Connected + Outbound Abandoned</p>
            </div>
          </div>
        </div>
        <div className="rounded-lg bg-white p-5 shadow border-l-4 border-blue-400">
          <div className="flex items-center gap-3">
            <PhoneIncoming className="h-7 w-7 text-blue-600 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-gray-500">Total Inbound Call Billable</p>
              <p className="text-2xl font-semibold text-gray-900">
                {(statistics.inboundAnswered + statistics.voicemailCalls).toLocaleString()}
              </p>
              <p className="text-xs text-gray-400">Counts Answered + VMs</p>
              <p className="text-xs text-blue-600 mt-0.5">{formatBillableMinutes(statistics.inboundBillableMinutes + statistics.voicemailBillableMinutes)}</p>
            </div>
          </div>
        </div>
        <div className="rounded-lg bg-white p-5 shadow border-l-4 border-purple-400">
          <div className="flex items-center gap-3">
            <PhoneOutgoing className="h-7 w-7 text-purple-600 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-gray-500">Total Outbound Call Billable</p>
              <p className="text-2xl font-semibold text-gray-900">
                {(statistics.outboundAnswered + statistics.outboundAbandoned).toLocaleString()}
              </p>
              <p className="text-xs text-gray-400">Counts Connected + Abandoned (Wait Time)</p>
              <p className="text-xs text-purple-600 mt-0.5">{formatBillableMinutes(statistics.outboundBillableMinutes)}</p>
            </div>
          </div>
        </div>
        <div className="rounded-lg bg-white p-5 shadow border-l-4 border-green-400">
          <div className="flex items-center gap-3">
            <Clock className="h-7 w-7 text-green-600 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-gray-500">Billable Time</p>
              <p className="text-xl font-semibold text-gray-900">{formatBillableMinutes(statistics.totalBillableMinutes)}</p>
              <p className="text-xs text-gray-400">Total Inbound + Total Outbound billable time</p>
            </div>
          </div>
        </div>
      </div>

      {/* Per-call averages row (inbound answered calls) */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-lg bg-white p-5 shadow border-l-4 border-indigo-400">
          <div className="flex items-center gap-3">
            <Clock className="h-7 w-7 text-indigo-600 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-gray-500">Avg In-Call Time</p>
              <p className="text-xl font-semibold text-gray-900">{formatBillableMinutes(statistics.avgInCallMinutes)}</p>
              <p className="text-xs text-gray-400">per answered call (in + out)</p>
            </div>
          </div>
        </div>
        <div className="rounded-lg bg-white p-5 shadow border-l-4 border-teal-400">
          <div className="flex items-center gap-3">
            <Clock className="h-7 w-7 text-teal-600 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-gray-500">Avg ACW</p>
              <p className="text-xl font-semibold text-gray-900">
                {statistics.avgAcwSeconds > 0
                  ? `${statistics.avgAcwSeconds.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}s`
                  : "0.0s"}
              </p>
              <p className="text-xs text-gray-400">per answered call (in + out)</p>
            </div>
          </div>
        </div>
        <div className="rounded-lg bg-white p-5 shadow border-l-4 border-purple-400">
          <div className="flex items-center gap-3">
            <Clock className="h-7 w-7 text-purple-600 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-gray-500">AHT</p>
              <p className="text-xl font-semibold text-gray-900">{formatBillableMinutes(statistics.ahtMinutes)}</p>
              <p className="text-xs text-gray-400">talk + hold + ACW (in + out)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards - Row 2 & 3: Inbound / Outbound breakdown */}
      <div className="rounded-lg bg-white shadow overflow-hidden">
        {/* Inbound Section — matches core definitions */}
        <div className="px-5 pt-4 pb-5 border-b border-gray-100">
          <div className="flex items-center gap-2 mb-3">
            <PhoneIncoming className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-semibold text-blue-700 uppercase tracking-wide">Inbound</span>
            <span className="ml-auto text-xs text-gray-400">{statistics.inboundCallsReceived.toLocaleString()} total calls received</span>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-7">
            <div className="rounded-lg bg-blue-50 border-l-4 border-blue-500 p-3">
              <p className="text-xs font-medium text-gray-500 mb-1">Total Calls Received</p>
              <p className="text-2xl font-semibold text-gray-900">{statistics.inboundCallsReceived.toLocaleString()}</p>
            </div>
            <div className="rounded-lg bg-amber-50 border-l-4 border-amber-500 p-3">
              <p className="text-xs font-medium text-gray-500 mb-1">Missed</p>
              <p className="text-2xl font-semibold text-gray-900">{(statistics.inboundMissed - statistics.callbackMissedCalls).toLocaleString()}</p>
            </div>
            <div className="rounded-lg bg-green-50 border-l-4 border-green-500 p-3">
              <p className="text-xs font-medium text-gray-500 mb-1">Inbound Answered</p>
              <p className="text-2xl font-semibold text-gray-900">{statistics.inboundAnswered.toLocaleString()}</p>
              <p className="text-xs text-blue-600 mt-0.5">{formatBillableMinutes(statistics.inboundBillableMinutes)}</p>
            </div>
            <div className="rounded-lg bg-orange-50 border-l-4 border-orange-400 p-3">
              <p className="text-xs font-medium text-gray-500 mb-1">Voicemails</p>
              <p className="text-2xl font-semibold text-gray-900">{statistics.voicemailCalls.toLocaleString()}</p>
              <p className="text-xs text-blue-600 mt-0.5">{formatBillableMinutes(statistics.voicemailBillableMinutes)}</p>
            </div>
            <div className="rounded-lg bg-teal-50 border-l-4 border-teal-500 p-3">
              <p className="text-xs font-medium text-gray-500 mb-1">Callbacks</p>
              <p className="text-2xl font-semibold text-teal-700">{statistics.callbackCalls.toLocaleString()}</p>
            </div>
            <div className="rounded-lg bg-orange-50 border-l-4 border-orange-500 p-3">
              <p className="text-xs font-medium text-gray-500 mb-1">Inbound Abandoned</p>
              <p className="text-2xl font-semibold text-gray-900">{(statistics.inboundAbandoned - statistics.callbackAbandonedCalls).toLocaleString()}</p>
            </div>
            <div className="rounded-lg bg-red-50 border-l-4 border-red-400 p-3">
              <p className="text-xs font-medium text-gray-500 mb-1">Test</p>
              <p className="text-2xl font-semibold text-red-600">{statistics.totalTestCalls.toLocaleString()}</p>
              <p className="text-xs text-gray-400 mt-0.5">{formatBillableMinutes(statistics.totalTestMinutes)} (not billable)</p>
            </div>
          </div>
        </div>

        {/* Outbound Section — matches core definitions */}
        <div className="px-5 pt-4 pb-5">
          <div className="flex items-center gap-2 mb-3">
            <PhoneOutgoing className="h-4 w-4 text-purple-600" />
            <span className="text-sm font-semibold text-purple-700 uppercase tracking-wide">Outbound</span>
            <span className="ml-auto text-xs text-gray-400">{statistics.outboundCallsReceived.toLocaleString()} total outbound</span>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <div className="rounded-lg bg-purple-50 border-l-4 border-purple-500 p-3">
              <p className="text-xs font-medium text-gray-500 mb-1">Total Outbound Calls</p>
              <p className="text-2xl font-semibold text-gray-900">{statistics.outboundCallsReceived.toLocaleString()}</p>
            </div>
            <div className="rounded-lg bg-green-50 border-l-4 border-green-400 p-3">
              <p className="text-xs font-medium text-gray-500 mb-1">Connected</p>
              <p className="text-2xl font-semibold text-gray-900">{statistics.outboundAnswered.toLocaleString()}</p>
              <p className="text-xs text-purple-600 mt-0.5">{formatBillableMinutes(statistics.outboundConnectedBillableMinutes)}</p>
            </div>
            <div className="rounded-lg bg-orange-50 border-l-4 border-orange-400 p-3">
              <p className="text-xs font-medium text-gray-500 mb-1">Outbound Abandoned</p>
              <p className="text-2xl font-semibold text-gray-900">{statistics.outboundAbandoned.toLocaleString()}</p>
              <p className="text-xs text-purple-600 mt-0.5">{formatBillableMinutes(statistics.outboundAbandonedWaitMinutes)} wait time</p>
              <p className="text-xs text-purple-600 mt-0.5">{formatBillableMinutes(statistics.outboundAbandonedWaitMinutes)} wait time</p>
            </div>
          </div>
        </div>
      </div>

      {/* Records Table */}
      <div className="rounded-lg bg-white shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Call Records</h2>
            <span className="text-sm text-gray-600">
              {format(new Date(startDate), "MMM d, yyyy")} - {format(new Date(endDate), "MMM d, yyyy")}
            </span>
          </div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Loading records...</span>
          </div>
        ) : records.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Database className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">
              No webhook-processed calls found for the selected date range.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 w-24">
                      Call ID
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 min-w-[120px]">
                      Queue
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hidden lg:table-cell">
                      Answered At (PT)
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hidden xl:table-cell">
                      Ended At (PT)
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hidden xl:table-cell">
                      Transfer (PT)
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Call Time
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Billable Call Time
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      ACW
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 sticky right-0 bg-gray-50 z-10 min-w-[200px]">
                      Status & Tags
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {records.map((record) => {
                    const callDurationMinutes = record.durationSeconds / 60;
                    return (
                      <tr key={record.callId} className="hover:bg-gray-50">
                        <td className="px-3 py-4 text-sm font-mono text-gray-900 truncate max-w-[100px]" title={record.callId}>
                          {record.callId}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-900 truncate max-w-[150px]" title={record.queueName || record.queueId || "N/A"}>
                          {record.queueName || record.queueId || "N/A"}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500 hidden lg:table-cell">
                          {record.answeredAt
                            ? formatInPacific(record.answeredAt)
                            : "—"}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500 hidden xl:table-cell">
                          {formatInPacific(record.endedAt)}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500 hidden xl:table-cell">
                          {record.externalTransferredAt
                            ? formatInPacific(record.externalTransferredAt)
                            : "—"}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-600">
                          {formatBillableMinutes(callDurationMinutes)}
                          <span className="text-xs text-gray-400 ml-1">(all)</span>
                        </td>
                        <td className="px-3 py-4 text-sm font-semibold text-green-700">
                          {formatBillableMinutes(record.billableMinutes)}
                          <span className="text-xs text-gray-400 ml-1">(billable)</span>
                        </td>
                        <td className="px-3 py-4 text-sm tabular-nums">
                          {record.afterCallWorkSeconds > 0 ? (
                            <span className="text-amber-700 font-medium">
                              {record.afterCallWorkSeconds}s
                            </span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-3 py-4 text-sm sticky right-0 bg-white z-10">
                          <div className="flex flex-wrap gap-1">
                            {/* Direction tag */}
                            {record.direction?.toLowerCase() === "inbound" ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                                <PhoneIncoming className="h-3 w-3" />
                                Inbound
                              </span>
                            ) : record.direction?.toLowerCase() === "outbound" ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-800">
                                <PhoneOutgoing className="h-3 w-3" />
                                Outbound
                              </span>
                            ) : null}
                            {/* Answer status — "Connected" for outbound (an agent placed the call and it connected), "Answered" for inbound */}
                            {record.isAnswered ? (
                              <span className="inline-flex rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                                {record.direction?.toLowerCase() === "outbound" ? "Connected" : "Answered"}
                              </span>
                            ) : (
                              <span className="inline-flex rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800">
                                Not Answered
                              </span>
                            )}
                            {/* Call type tags */}
                            {record.isTestCall && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800">
                                <Beaker className="h-3 w-3" />
                                Test
                              </span>
                            )}
                            {record.isVoicemail && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-orange-800">
                                <Voicemail className="h-3 w-3" />
                                Voicemail
                              </span>
                            )}
                            {record.isAbandoned && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800">
                                <AlertTriangle className="h-3 w-3" />
                                Abandoned
                              </span>
                            )}
                            {record.isMissed && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800">
                                <PhoneMissed className="h-3 w-3" />
                                Missed
                              </span>
                            )}
                            {record.externalTransferredAt && (
                              <span className="inline-flex rounded-full bg-cyan-100 px-2 py-1 text-xs font-medium text-cyan-800">
                                Transferred
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 bg-white px-6 py-4">
                <div className="flex flex-1 justify-between sm:hidden">
                  <button
                    onClick={() => fetchWebhookCalls(pagination.page - 1)}
                    disabled={pagination.page === 1 || loading}
                    className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => fetchWebhookCalls(pagination.page + 1)}
                    disabled={
                      pagination.page === pagination.totalPages || loading
                    }
                    className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing{" "}
                      <span className="font-medium">
                        {(pagination.page - 1) * pagination.limit + 1}
                      </span>{" "}
                      to{" "}
                      <span className="font-medium">
                        {Math.min(
                          pagination.page * pagination.limit,
                          pagination.total
                        )}
                      </span>{" "}
                      of <span className="font-medium">{pagination.total}</span>{" "}
                      results
                    </p>
                  </div>
                  <div>
                    <nav
                      className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                      aria-label="Pagination"
                    >
                      <button
                        onClick={() => fetchWebhookCalls(pagination.page - 1)}
                        disabled={pagination.page === 1 || loading}
                        className="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                        .filter(
                          (page) =>
                            page === 1 ||
                            page === pagination.totalPages ||
                            (page >= pagination.page - 1 &&
                              page <= pagination.page + 1)
                        )
                        .map((page, idx, arr) => (
                          <div key={page}>
                            {idx > 0 && arr[idx - 1] !== page - 1 && (
                              <span className="relative inline-flex items-center border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700">
                                ...
                              </span>
                            )}
                            <button
                              onClick={() => fetchWebhookCalls(page)}
                              disabled={loading}
                              className={`relative inline-flex items-center border px-4 py-2 text-sm font-medium focus:z-20 ${
                                pagination.page === page
                                  ? "z-10 border-blue-500 bg-blue-50 text-blue-600"
                                  : "border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
                              }`}
                            >
                              {page}
                            </button>
                          </div>
                        ))}
                      <button
                        onClick={() => fetchWebhookCalls(pagination.page + 1)}
                        disabled={
                          pagination.page === pagination.totalPages || loading
                        }
                        className="relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      </>}

      {showSendEmailModal && (() => {
        const isAiSend = sendEmailReportType === "ai_minutes_usage";
        const aiSelectedIds = Object.keys(aiSelectedCustomers).filter((id) => aiSelectedCustomers[id]);
        return (
          <SendEmailReportModal
            reportType={sendEmailReportType}
            availableCustomers={isAiSend ? aiCustomers.map((c) => ({ id: c.id, name: c.name })) : customers}
            defaultCustomerId={
              isAiSend
                ? (aiSelectedIds.length === 1 ? aiSelectedIds[0] : undefined)
                : (selectedCount === 1 ? getSelectedCustomerIds()[0] : undefined)
            }
            defaultStartDate={startDate}
            defaultEndDate={endDate}
            onClose={() => setShowSendEmailModal(false)}
          />
        );
      })()}
    </div>
  );
}
